import { FC, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import classes from "./teamsList.module.scss";
import { ITeam, teamService } from "../../../../entities/team";
import { LoaderSpinner } from "../../../../shared/ui/spinner";
import { SearchItems } from "../../../../features/searchItems";
import { WorkTgFilter } from "../../../workTgFilter";
import { TEAM_ROUTE } from "../../../../app/router/routes";
import { TeamFormModal } from "../teamFormModal/TeamFormModal";
import { TeamDeleteModal } from "../teamDeleteModal/TeamDeleteModal";
import { initialFormState, ITeamFormState } from "../../lib/types";
import { useTeamsCommon } from "../../lib/useTeamsCommon";
import { getTeamSearchValue } from "../../lib/search";

export const TeamsWidget: FC = () => {
    const navigate = useNavigate()
    const [params, setParams] = useSearchParams()
    const [isLoading, setIsLoading] = useState(true)
    const [teams, setTeams] = useState<ITeam[]>([])
    const [teamsSearch, setTeamsSearch] = useState<ITeam[]>([])
    const [teamModalOpen, setTeamModalOpen] = useState(false)
    const [editingTeamId, setEditingTeamId] = useState<string | null>(null)
    const [form, setForm] = useState<ITeamFormState>(initialFormState)
    const [formError, setFormError] = useState('')
    const [deletingTeam, setDeletingTeam] = useState<ITeam | null>(null)
    const workTgIdsFromParams = useMemo(
        () => params
            .getAll('work_tg_ids')
            .map(id => parseInt(id, 10))
            .filter(id => !isNaN(id) && id > 0),
        [params]
    )
    const workTgIdsFromParamsKey = workTgIdsFromParams.join(',')
    const [selectedWorkTgIds, setSelectedWorkTgIds] = useState<number[]>(workTgIdsFromParams)
    const {canRead, canListWorkTgs, canUseTeamForm, handleError} = useTeamsCommon()

    const syncSearch = (items: ITeam[]) => {
        setTeamsSearch(items)
    }

    const loadTeams = async (work_tg_ids: number[] = []) => {
        try{
            setIsLoading(true)
            const items = await teamService.list(work_tg_ids)
            console.log(items)
            const sortedItems = items.sort((a, b) => a.name.localeCompare(b.name))
            setTeams(sortedItems)
            syncSearch(sortedItems)
        }
        catch(e){
            handleError(e, 'Ошибка при получении списка звеньев')
        }
        finally{
            setIsLoading(false)
        }
    }

    useEffect(() => {
        setSelectedWorkTgIds(prev => {
            if(prev.length === workTgIdsFromParams.length && prev.every((id, index) => id === workTgIdsFromParams[index])){
                return prev
            }

            return workTgIdsFromParams
        })
    }, [workTgIdsFromParams, workTgIdsFromParamsKey])

    useEffect(() => {
        if(canRead){
            loadTeams(workTgIdsFromParams)
        }
        else{
            setIsLoading(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canRead, workTgIdsFromParamsKey])

    const setFilterParams = (nextWorkTgIds: number[]) => {
        const newParams = new URLSearchParams(params)
        newParams.delete('work_tg_ids')

        nextWorkTgIds.forEach(id => {
            if(id > 0){
                newParams.append('work_tg_ids', String(id))
            }
        })

        setParams(newParams)
    }

    const onWorkTgIdsChange = (nextWorkTgIds: number[]) => {
        setSelectedWorkTgIds(nextWorkTgIds)
        setFilterParams(nextWorkTgIds)
    }

    const openCreateModal = () => {
        setEditingTeamId(null)
        setForm(initialFormState)
        setFormError('')
        setTeamModalOpen(true)
    }

    return (
        <section className={classes.listPage}>
            <section className={classes.panel}>
                <section className={classes.panelHeader}>
                    <section className={classes.panelTitle}>Звенья</section>
                    {canUseTeamForm && (
                        <button className={classes.createButton} aria-label="Создать звено" onClick={openCreateModal}>+</button>
                    )}
                </section>

                <section className={classes.search}>
                    <SearchItems
                        placeholder="Поиск по названию, ТГ или координатору"
                        items={teams}
                        setItems={setTeamsSearch}
                        getSearchValue={getTeamSearchValue}
                    />
                </section>

                {canListWorkTgs && (
                    <section className={classes.filters}>
                        <section className={classes.filtersTitle}>Фильтры</section>
                        <WorkTgFilter
                            setWorkTgIds={onWorkTgIdsChange}
                            initWorkTgIds={selectedWorkTgIds}
                        />
                    </section>
                )}
            </section>

            <section className={classes.panel}>
                {
                    isLoading
                        ? 
                    <section className={classes.empty}><LoaderSpinner /></section>
                        : 
                    teamsSearch.length > 0
                        ? 
                    <section className={classes.wideList}>
                        {teamsSearch.map(item => (
                            <button
                                key={item.id}
                                type="button"
                                className={classes.wideItem}
                                onClick={() => navigate(TEAM_ROUTE.path.replace(':id', String(item.id)))}
                            >
                                <section className={classes.itemMain}>
                                    <section className={classes.itemName}>{item.name}</section>
                                    <section className={classes.itemMeta}>
                                        ТГ: {item.tgAccounts.map(tg => tg.title || tg.username).join(', ') || 'Не указаны'}
                                    </section>
                                    <section className={classes.itemMeta}>
                                        Координаторы: {item.coordinators.map(coordinator => coordinator.fullName).join(', ') || 'Не указаны'}
                                    </section>
                                </section>
                            </button>
                        ))}
                    </section>
                        : 
                    <section className={classes.empty}>Звенья не найдены</section>
                }
            </section>

            <TeamFormModal
                open={teamModalOpen}
                setOpen={(open) => {
                    setTeamModalOpen(open)
                    if(!open){
                        setEditingTeamId(null)
                        setForm(initialFormState)
                        setFormError('')
                    }
                }}
                editingTeamId={editingTeamId}
                form={form}
                setForm={setForm}
                formError={formError}
                setFormError={setFormError}
                onSaveSuccess={async () => {
                    await loadTeams()
                }}
            />
            <TeamDeleteModal
                team={deletingTeam}
                setTeam={setDeletingTeam}
                onDeleteSuccess={async () => {
                    await loadTeams()
                }}
            />
        </section>
    )
}
